/**
 * Render resolution that gives way when frames run slow (2026-10-15, QA #9:
 * "heavy FPS drops"; CLAUDE.md: 60 FPS on a mid-range 2022 Android phone).
 *
 * The post stack's cost is per physical pixel, so pixel ratio is the one knob
 * that scales the whole frame without changing what is drawn. This watches the
 * interval between rendered frames and steps the ratio down a notch when the
 * median of a window is clearly below 60fps.
 *
 * Stepping back *up* cannot wait for spare headroom (2026-10-16): the render
 * pacer holds a high-refresh display at ~60fps, so the interval sits at
 * ~16.7ms however fast the GPU is, and a "faster than 11ms" rule meant one
 * slow window cost the player resolution for the rest of the run. Instead a
 * run of on-time windows (median <= `ON_TIME_MS`) earns the notch back. If
 * that notch immediately proves too much — the very next window is slow — the
 * run required next time doubles, so a GPU sitting on the boundary settles
 * at the lower ratio instead of oscillating.
 *
 * Median, not mean: a single shader-compile hitch must not cost the player
 * resolution. And `sample` ignores gaps over 250ms (a tab switch, the run
 * menu) for the same reason.
 */
export const ADAPTIVE_WINDOW = 90;
export const STEP_DOWN_ABOVE_MS = 21; // ~48fps
export const ON_TIME_MS = 17.5; // a 60fps frame plus scheduling slack
export const ON_TIME_WINDOWS_TO_STEP_UP = 4; // ~6s at 60fps
export const MAX_ON_TIME_WINDOWS = 32;
export const RATIO_STEP = 0.25;

export class AdaptiveResolution {
  private readonly samples = new Float64Array(ADAPTIVE_WINDOW);
  private count = 0;
  private last = -1;
  private ratio: number;
  private onTimeStreak = 0;
  private windowsNeeded = ON_TIME_WINDOWS_TO_STEP_UP;
  private justSteppedUp = false;

  constructor(private readonly maxRatio: number, private readonly minRatio: number) {
    this.ratio = maxRatio;
  }

  get current(): number { return this.ratio; }

  /** Call once per rendered frame. Returns the new pixel ratio when it changes, else null. */
  sample(nowMs: number): number | null {
    const prev = this.last;
    this.last = nowMs;
    if (prev < 0) return null;
    const dt = nowMs - prev;
    if (dt > 250) return null;
    this.samples[this.count++] = dt;
    if (this.count < ADAPTIVE_WINDOW) return null;
    this.count = 0;
    const median = [...this.samples].sort((a, b) => a - b)[ADAPTIVE_WINDOW >> 1];
    const steppedUpLastWindow = this.justSteppedUp;
    this.justSteppedUp = false;

    let next = this.ratio;
    if (median > STEP_DOWN_ABOVE_MS) {
      this.onTimeStreak = 0;
      if (steppedUpLastWindow) this.windowsNeeded = Math.min(MAX_ON_TIME_WINDOWS, this.windowsNeeded * 2);
      next = Math.max(this.minRatio, this.ratio - RATIO_STEP);
    } else if (median <= ON_TIME_MS && this.ratio < this.maxRatio) {
      this.onTimeStreak += 1;
      if (this.onTimeStreak >= this.windowsNeeded) {
        this.onTimeStreak = 0;
        this.justSteppedUp = true;
        next = Math.min(this.maxRatio, this.ratio + RATIO_STEP);
      }
    } else {
      this.onTimeStreak = 0;
    }
    if (next === this.ratio) return null;
    this.ratio = next;
    return next;
  }
}
